import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/campus/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const TABS = ["Dashboard","Verifications","Decisions","Users","Contact","Coupons"];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("Dashboard");
  const [stats, setStats] = useState({});
  const [amb, setAmb] = useState([]);
  const [dec, setDec] = useState([]);
  const [users, setUsers] = useState([]);
  const [contact, setContact] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const nav = useNavigate();

  useEffect(()=>{
    if (!loading && (!user || user.role !== "admin")) { nav("/"); return; }
    if (user) load();
  }, [user, loading]);

  const load = async () => {
    try {
      const [s, a, d, u, c, cp] = await Promise.all([api.adminStats(), api.adminAmbassadors(), api.adminDecisions(), api.adminUsers(), api.adminContact(), api.adminCoupons()]);
      setStats(s); setAmb(a); setDec(d); setUsers(u); setContact(c); setCoupons(cp);
    } catch (e) { console.error(e); }
  };

  const verify = async (uid, status) => {
    let reason = null;
    if (status === "rejected") { reason = prompt("Rejection reason?"); if (!reason) return; }
    try { await api.adminVerify({ user_id: uid, status, rejection_reason: reason }); toast.success("Updated"); load(); }
    catch { toast.error("Failed"); }
  };

  if (!user || user.role !== "admin") return null;

  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      <Navbar/>
      <div className="pt-20 px-6 md:px-12 max-w-7xl mx-auto pb-20">
        <h1 className="font-display text-3xl font-bold mb-6">Admin Panel</h1>
        <div className="flex flex-wrap gap-2 mb-6 border-b border-white/10 pb-2">
          {TABS.map(t=><button key={t} data-testid={`admin-tab-${t}`} onClick={()=>setTab(t)} className={`px-4 py-2 rounded-full text-sm ${tab===t?"bg-[#2563EB]":"bg-white/5"}`}>{t}</button>)}
        </div>

        {tab==="Dashboard" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Card label="Students" value={stats.students||0}/>
            <Card label="Campus Ambassadors" value={stats.ambassadors||0}/>
            <Card label="Conversations" value={stats.conversations||0}/>
            <Card label="Decisions" value={stats.decisions||0}/>
            <Card label="Pending Verifications" value={stats.pending_verifications||0}/>
            <Card label="Pending Decisions" value={stats.pending_decisions||0}/>
          </div>
        )}

        {tab==="Verifications" && (
          <div className="glass rounded-2xl overflow-x-auto"><table className="w-full text-sm"><thead className="bg-white/5"><tr><th className="text-left p-3">Name</th><th className="text-left">College</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>{amb.map(a=>(
              <tr key={a.user_id} className="border-t border-white/5"><td className="p-3">{a.full_name}<div className="text-xs text-slate-500">{a.email}</div></td><td>{a.college_name}<div className="text-xs text-slate-500">{a.course} · {a.year}</div></td>
              <td className="text-center"><span className={`text-xs px-2 py-1 rounded-full capitalize ${a.verification_status==="verified"?"bg-emerald-500/15 text-emerald-400":a.verification_status==="rejected"?"bg-red-500/15 text-red-400":"bg-amber-500/15 text-amber-400"}`}>{a.verification_status}</span></td>
              <td className="text-center"><div className="flex gap-2 justify-center">{a.college_id_doc_url && <a href={a.college_id_doc_url} target="_blank" rel="noreferrer" className="text-xs text-blue-400">View ID</a>}<button onClick={()=>verify(a.user_id,"verified")} className="text-xs px-2 py-1 rounded bg-emerald-500/15 text-emerald-400">Approve</button><button onClick={()=>verify(a.user_id,"rejected")} className="text-xs px-2 py-1 rounded bg-red-500/15 text-red-400">Reject</button></div></td></tr>
            ))}</tbody></table></div>
        )}

        {tab==="Decisions" && (
          <div className="glass rounded-2xl overflow-x-auto"><table className="w-full text-sm"><thead className="bg-white/5"><tr><th className="text-left p-3">Student</th><th>Phone</th><th>College</th><th>Time</th><th>Status</th></tr></thead>
            <tbody>{dec.map(d=>(
              <tr key={d.id} className="border-t border-white/5"><td className="p-3">{d.student_name}</td><td>{d.student_phone}</td><td>{d.college_name}</td><td className="text-center capitalize">{d.preferred_contact_time}</td>
              <td><select value={d.status} onChange={e=>{api.adminUpdateDecision(d.id, e.target.value).then(load);}} className="bg-[#1E293B] border border-white/10 rounded px-2 py-1 text-xs"><option value="pending">Pending</option><option value="in_progress">In Progress</option><option value="completed">Completed</option></select></td></tr>
            ))}</tbody></table></div>
        )}

        {tab==="Users" && (
          <div className="glass rounded-2xl overflow-x-auto"><table className="w-full text-sm"><thead className="bg-white/5"><tr><th className="text-left p-3">Name</th><th>Email</th><th>Role</th><th>Banned</th><th>Action</th></tr></thead>
            <tbody>{users.map(u=>(
              <tr key={u.id} className="border-t border-white/5"><td className="p-3">{u.full_name}</td><td>{u.email}</td><td className="capitalize">{u.role||"—"}</td><td className="text-center">{u.is_banned?"Yes":"No"}</td><td><button onClick={()=>api.adminBan(u.id, !u.is_banned).then(load)} className="text-xs px-2 py-1 rounded bg-white/5">{u.is_banned?"Unban":"Ban"}</button></td></tr>
            ))}</tbody></table></div>
        )}

        {tab==="Contact" && (
          <div className="space-y-3">{contact.map(c=>(
            <div key={c.id} className="glass rounded-2xl p-4"><div className="flex justify-between"><div className="font-semibold">{c.full_name} · {c.email}</div><span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString()}</span></div><p className="text-slate-300 text-sm mt-2">{c.message}</p></div>
          ))}{contact.length===0 && <div className="text-slate-400 text-sm">No submissions yet.</div>}</div>
        )}

        {tab==="Coupons" && (
          <div className="glass rounded-2xl overflow-x-auto"><table className="w-full text-sm"><thead className="bg-white/5"><tr><th className="text-left p-3">Code</th><th>Service</th><th>Discount</th><th>Used</th><th>Active</th></tr></thead>
            <tbody>{coupons.map(c=>(
              <tr key={c.code} className="border-t border-white/5"><td className="p-3 font-mono">{c.code}</td><td className="capitalize">{c.service_type}</td><td>{c.discount_value}{c.discount_type==="percent"?"%":""}</td><td className="text-center">{c.used_count}</td><td className="text-center">{c.is_active?"✓":"✗"}</td></tr>
            ))}</tbody></table></div>
        )}
      </div>
    </div>
  );
}

const Card = ({label, value}) => <div className="glass rounded-2xl p-5"><div className="text-xs text-slate-400">{label}</div><div className="font-display text-3xl font-bold mt-1">{value}</div></div>;
